import React, { useState, useEffect, useRef } from 'react';

function AnimatedNumber({ value, decimals = 0, duration = 1200 }) {
  const [display, setDisplay] = useState(value);
  const prevRef = useRef(value);

  useEffect(() => {
    const prev = prevRef.current;
    if (prev === value) return;
    prevRef.current = value;

    const startTime = Date.now();
    const diff = value - prev;

    const animate = () => {
      const elapsed = Date.now() - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3); // ease-out cubic
      setDisplay(prev + diff * eased);
      if (progress < 1) requestAnimationFrame(animate);
    };
    requestAnimationFrame(animate);
  }, [value, duration]);

  return decimals > 0
    ? display.toFixed(decimals)
    : Math.floor(display).toLocaleString();
}

export default function AnimatedBurnCounter({ burn }) {
  if (!burn) return null;

  const burned = burn.total_burned_tokens ?? burn.total_burned ?? 0;
  const currentSupply = burn.current_supply ?? 1_000_000_000;
  const burnFloor = burn.burn_floor ?? 500_000_000;
  const maxBurnable = burn.max_burnable ?? 500_000_000;
  const progressPct = Math.min((burned / Math.max(maxBurnable, 1)) * 100, 100);
  const supplyPct = ((currentSupply - burnFloor) / maxBurnable) * 100;

  return (
    <div className="bg-slate-900/60 border border-orange-900/30 rounded-xl p-3 sm:p-5 col-span-1 sm:col-span-2 overflow-hidden">
      <div className="flex items-center justify-between mb-1">
        <h3 className="text-xs text-orange-400 uppercase tracking-wider font-medium">Burn Tracker</h3>
        <span className="text-[10px] text-slate-500">{burn.burn_active ? 'Active' : 'Floor reached'}</span>
      </div>

      <div className="flex flex-col sm:flex-row sm:items-end gap-2 sm:gap-4 mt-3">
        {/* Main burn number */}
        <div className="min-w-0">
          <p className="text-2xl sm:text-4xl font-bold bg-gradient-to-r from-orange-400 via-red-400 to-orange-500 bg-clip-text text-transparent truncate"
             style={{ backgroundSize: '200% 100%', animation: 'burnGradient 3s ease infinite' }}>
            <AnimatedNumber value={burned} decimals={burned < 1 ? 6 : burned < 1000 ? 2 : 0} />
          </p>
          <p className="text-[10px] text-slate-500 mt-0.5">$THANE burned forever</p>
        </div>

        {/* Supply counter */}
        <div className="sm:ml-auto sm:text-right min-w-0">
          <p className="text-base sm:text-lg font-mono text-slate-300 truncate">
            <AnimatedNumber value={currentSupply} />
          </p>
          <p className="text-[10px] text-slate-500">circulating supply</p>
        </div>
      </div>

      {/* Progress bar */}
      <div className="mt-4">
        <div className="w-full h-2 bg-slate-800 rounded-full overflow-hidden relative">
          {/* Burn progress */}
          <div
            className="absolute left-0 top-0 h-full rounded-full transition-all duration-1000"
            style={{
              width: `${Math.max(progressPct, 0.5)}%`,
              background: 'linear-gradient(90deg, #f97316, #ef4444, #f97316)',
              backgroundSize: '200% 100%',
              animation: 'burnGradient 2s ease infinite',
            }}
          />
          {/* Supply remaining indicator */}
          <div
            className="absolute top-0 h-full bg-purple-500/20 rounded-full transition-all duration-1000"
            style={{ width: `${supplyPct}%`, right: 0 }}
          />
        </div>
        <div className="flex justify-between mt-1.5 text-[9px] text-slate-600">
          <span>0 burned</span>
          <span className="text-orange-500/60">
            {progressPct > 0.001 ? `${progressPct.toFixed(4)}%` : 'Starting...'}
          </span>
          <span>Floor: 500M</span>
        </div>
      </div>

      {/* Fee split breakdown */}
      <div className="grid grid-cols-3 gap-2 sm:gap-3 mt-3 sm:mt-4 pt-3 border-t border-slate-800/50">
        <div>
          <p className="text-[10px] text-slate-500">Fee rate</p>
          <p className="text-xs font-mono text-white">3% <span className="text-slate-500">/ 1.5%</span></p>
        </div>
        <div>
          <p className="text-[10px] text-slate-500">Burn share</p>
          <p className="text-xs font-mono text-orange-400">30% of fees</p>
        </div>
        <div>
          <p className="text-[10px] text-slate-500">Treasury</p>
          <p className="text-xs font-mono text-blue-400">70% of fees</p>
        </div>
      </div>

      <style>{`
        @keyframes burnGradient {
          0%, 100% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
        }
      `}</style>
    </div>
  );
}
